/**
 * Your games: every game played on this site, newest first — the unfinished
 * ones on top to carry on, the rest to find, review, star, note, copy out or
 * forget.
 *
 * The games themselves are `../data/my-games.js`, written by the three play
 * pages as they go. This page reads them, draws them from `localStorage` at
 * once, and draws again if the account brings down games from another device.
 *
 * FINDING ONE: a search over who it was against, the opening (named from
 * Lichess's public-domain table, `../data/openings.js`, loaded the first time
 * the page has games to name), how it ended and your own note; the filters
 * underneath; and head to head, which is every opponent you have played more
 * than once, with the score.
 */

import { mountNav } from '../nav.mjs';
import { accountUser } from '../account-link.js';
import {
  LEVEL_EVENT, NOTE_MAX, accountLevel, allGames, annotateGame, continueOf, eventOf, forgetGame, opponentOf, outcomeFor,
  reviewUrl, syncGames,
} from '../data/my-games.js';
import {
  formatChange, formatLevel, ratedGame, replayLevels,
} from '../level.js';
import { loadOpenings, openingOf } from '../data/openings.js';

const $ = (id) => document.getElementById(id);
mountNav($('nav-slot'), { base: '../' });

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const KIND_WORDS = { engine: 'vs the engine', friend: 'with a friend', lichess: 'on Lichess' };

/**
 * What each rated game did to your level: the account's figure where it has
 * given one (`accountLevel`), otherwise this browser's own replay of the games
 * it holds. Worked out once per draw.
 */
let levels = { level: 0, games: 0, per: new Map() };

function workOutLevels(games) {
  const local = replayLevels(games.map(ratedGame).filter(Boolean));
  const per = new Map(local.per.map((p) => [p.id, p]));
  const account = accountLevel();
  for (const [id, p] of Object.entries(account?.per || {})) per.set(id, { ...per.get(id), ...p });
  levels = { level: account ? account.level : local.level, games: account ? account.games : local.games, per };
}

/** What the filter row offers. `test` decides which finished games it keeps. */
const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'Won', label: 'Won', test: (r) => outcomeFor(r) === 'Won' },
  { id: 'Lost', label: 'Lost', test: (r) => outcomeFor(r) === 'Lost' },
  { id: 'Drawn', label: 'Drawn', test: (r) => outcomeFor(r) === 'Drawn' },
  { id: 'star', label: '★ Starred', test: (r) => Boolean(r.star) },
  { id: 'rated', label: 'Rated', test: (r) => levels.per.has(r.id) },
  { id: 'engine', label: 'Engine', test: (r) => r.kind === 'engine' },
  { id: 'friend', label: 'Friends', test: (r) => r.kind === 'friend' },
  { id: 'lichess', label: 'Lichess', test: (r) => r.kind === 'lichess' },
];

/** How many finished games are drawn before "Show more". */
const PAGE = 40;

let filter = 'all';
let query = '';
let shown = PAGE;
/** The opening table, once it has loaded; until then rows show the first moves. */
let openings = null;
/** Which game's note is open for writing, if any. */
let writing = null;

/** "1.e4 e5 2.Nf3 Nc6 …", enough of the opening to recognise a game by. */
function firstMoves(moves, plies = 6) {
  const parts = [];
  for (let i = 0; i < Math.min(plies, moves.length); i += 1) {
    parts.push(i % 2 === 0 ? `${i / 2 + 1}.${moves[i]}` : moves[i]);
  }
  return parts.join(' ') + (moves.length > plies ? ' …' : '');
}

const openingFor = (r) => openingOf(r.moves, openings);

function when(at) {
  const d = new Date(at);
  const days = Math.floor((Date.now() - at) / 864e5);
  if (days < 1 && d.getDate() === new Date().getDate()) {
    return `Today, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
}

function levelChip(r) {
  const p = levels.per.get(r.id);
  if (!p) return '';
  const c = Number(p.change);
  return ` · <span class="lv-change ${c > 0 ? 'up' : c < 0 ? 'down' : 'flat'}" title="Level ${formatLevel(p.after)} after this game">${esc(formatChange(c))}</span>`;
}

/** Everything a search looks through for one game, lowercased. */
function haystack(r) {
  const o = openingFor(r);
  return [opponentOf(r), KIND_WORDS[r.kind], r.by, o?.name, o?.eco, r.note, outcomeFor(r)]
    .filter(Boolean).join(' ').toLowerCase();
}

const matchesQuery = (r) => !query || query.split(/\s+/).every((word) => haystack(r).includes(word));

/** One game as a row. Its buttons are wired by `wire`, below. */
function row(r) {
  const outcome = outcomeFor(r);
  const moves = Math.ceil(r.moves.length / 2);
  const go = continueOf(r);
  const o = openingFor(r);
  const opening = o
    ? `<span class="mg-opening"><span class="mg-eco">${esc(o.eco)}</span> ${esc(o.name)}</span>`
    : `<span class="mg-opening mg-moves">${esc(firstMoves(r.moves))}</span>`;
  const note = writing === r.id
    ? `<form class="mg-note-form" data-note-form>
        <label class="sr-only" for="note-${esc(r.id)}">Your note on this game</label>
        <textarea id="note-${esc(r.id)}" name="note" rows="2" maxlength="${NOTE_MAX}" placeholder="Only you see this.">${esc(r.note || '')}</textarea>
        <div class="mg-note-acts"><button type="submit" class="ctl primary">Save note</button><button type="button" class="ctl" data-act="note-cancel">Cancel</button></div>
      </form>`
    : r.note ? `<p class="mg-note">${esc(r.note)}</p>` : '';
  return `<li class="mg-game${r.star ? ' starred' : ''}" data-id="${esc(r.id)}">
    <div class="mg-main">
      <span class="mg-outcome ${outcome.toLowerCase()}">${esc(outcome)}</span>
      <span class="mg-title">${esc(opponentOf(r))} <span class="mg-kind">${esc(KIND_WORDS[r.kind] || '')}</span></span>
      <span class="mg-meta">${esc(when(r.started || r.at))} · ${esc(plural(moves, 'move'))} · you had ${r.you === 'b' ? 'Black' : 'White'}${r.by && r.over ? ` · ${esc(r.by)}` : ''}${levelChip(r)}</span>
      ${opening}
      ${note}
    </div>
    <div class="mg-acts">
      ${go ? `<a class="ctl primary" href="${esc(go.href)}">${esc(go.label)}</a>` : ''}
      ${r.moves.length >= 2 ? '<a class="ctl" data-act="review">Review</a>' : ''}
      ${r.over ? `<button type="button" class="ctl mg-star" data-act="star" aria-pressed="${Boolean(r.star)}" aria-label="${r.star ? 'Unstar' : 'Star'} this game" title="${r.star ? 'Unstar' : 'Star'}">${r.star ? '★' : '☆'}</button>` : ''}
      ${r.over ? `<button type="button" class="ctl" data-act="note">${r.note ? 'Edit note' : 'Note'}</button>` : ''}
      <button type="button" class="ctl" data-act="pgn">Copy PGN</button>
      <button type="button" class="ctl mg-forget" data-act="forget" aria-label="Forget this game" title="Forget this game">✕</button>
    </div>
  </li>`;
}

/** The details `play/keep.js` needs to write one game as PGN. */
const pgnDetails = (r) => {
  const d = new Date(r.started || r.at);
  const p = (n, w) => String(n).padStart(w, '0');
  return {
    moves: r.moves,
    result: r.result,
    white: r.white,
    black: r.black,
    event: eventOf(r),
    date: `${p(d.getFullYear(), 4)}.${p(d.getMonth() + 1, 2)}.${p(d.getDate(), 2)}`,
  };
};

/** Give a drawn list's buttons something to do. */
function wire(list, byId) {
  for (const li of list.querySelectorAll('.mg-game')) {
    const r = byId.get(li.dataset.id);
    const review = li.querySelector('[data-act="review"]');
    if (review) reviewUrl(r).then((href) => { review.href = href; }).catch(() => review.remove());

    li.querySelector('[data-act="pgn"]').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const { copyPgn } = await import('../play/keep.js');
      const ok = await copyPgn(pgnDetails(r));
      button.textContent = ok ? 'Copied' : 'Could not copy';
      setTimeout(() => { button.textContent = 'Copy PGN'; }, 1600);
    });

    li.querySelector('[data-act="star"]')?.addEventListener('click', () => {
      annotateGame(r.id, { star: !r.star });
      draw();
    });

    li.querySelector('[data-act="note"]')?.addEventListener('click', () => {
      writing = r.id;
      draw();
      const box = document.getElementById(`note-${r.id}`);
      box?.focus();
      box?.setSelectionRange(box.value.length, box.value.length);
    });
    li.querySelector('[data-act="note-cancel"]')?.addEventListener('click', () => { writing = null; draw(); });
    li.querySelector('[data-note-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      annotateGame(r.id, { note: new FormData(event.currentTarget).get('note') });
      writing = null;
      draw();
    });

    li.querySelector('[data-act="forget"]').addEventListener('click', () => {
      if (!confirm(`Forget your game against ${opponentOf(r)}? It goes from every device you are signed in on.`)) return;
      forgetGame(r.id);
      draw();
    });
  }
}

function drawFilters(finished) {
  const counts = new Map(FILTERS.map((f) => [f.id, finished.filter(f.test).length]));
  $('mg-filters').innerHTML = FILTERS
    // A filter that would show nothing is not offered, except "All".
    .filter((f) => f.id === 'all' || counts.get(f.id))
    .map((f) => `<button type="button" class="mg-filter${f.id === filter ? ' on' : ''}" data-filter="${f.id}" aria-pressed="${f.id === filter}">`
      + `${esc(f.label)} <span class="mg-count">${counts.get(f.id)}</span></button>`)
    .join('');
  $('mg-filters').hidden = finished.length < 2;
  $('mg-find').hidden = finished.length < 2;
}

/**
 * Everybody you have played more than once, most games first, with your score
 * against them. Pressing one searches for them.
 */
function drawHeadToHead(finished) {
  const by = new Map();
  for (const r of finished) {
    const outcome = outcomeFor(r);
    if (!['Won', 'Drawn', 'Lost'].includes(outcome)) continue;
    const name = opponentOf(r);
    if (name === 'Your friend' || name === 'Opponent') continue;
    const h = by.get(name) || { name, kind: r.kind, games: 0, won: 0, drawn: 0, lost: 0 };
    h.games += 1;
    h[outcome.toLowerCase()] += 1;
    by.set(name, h);
  }
  const rows = [...by.values()].filter((h) => h.games >= 2)
    .sort((a, b) => b.games - a.games || a.name.localeCompare(b.name)).slice(0, 10);
  $('mg-h2h').hidden = !rows.length;
  $('mg-h2h-list').innerHTML = rows.map((h) => {
    const points = h.won + h.drawn / 2;
    return `<li><button type="button" class="mg-h2h-row" data-who="${esc(h.name)}">`
      + `<span class="pf-li-main"><b>${esc(h.name)}</b> <span class="mg-kind">${esc(KIND_WORDS[h.kind] || '')}</span></span>`
      + `<span class="pf-li-side">${esc(plural(h.games, 'game'))} · ${h.won} won, ${h.drawn} drawn, ${h.lost} lost · `
      + `${points.toLocaleString()}–${(h.games - points).toLocaleString()}</span></button></li>`;
  }).join('');
}

/** The finished games the search and the filter leave, newest first. */
function matching(finished) {
  const test = FILTERS.find((f) => f.id === filter).test;
  return finished.filter(test).filter(matchesQuery);
}

function draw() {
  const games = allGames();
  workOutLevels(games);
  const byId = new Map(games.map((r) => [r.id, r]));
  const unfinished = games.filter((r) => !r.over);
  const finished = games.filter((r) => r.over);

  $('mg-unfinished').hidden = !unfinished.length;
  $('mg-unfinished-list').innerHTML = unfinished.map(row).join('');
  wire($('mg-unfinished-list'), byId);

  if (!FILTERS.some((f) => f.id === filter && (f.id === 'all' || finished.some(f.test)))) filter = 'all';
  drawFilters(finished);
  drawHeadToHead(finished);
  const found = matching(finished);
  $('mg-list').innerHTML = found.slice(0, shown).map(row).join('');
  wire($('mg-list'), byId);
  $('mg-more').hidden = found.length <= shown;
  $('mg-download').hidden = !found.length;
  $('mg-download').textContent = `Download ${found.length === finished.length ? 'all' : 'these'} ${plural(found.length, 'game')} as PGN`;

  if (!games.length) {
    // Lichess left out in the Android and iOS apps, which cannot sign in to it (`app/net.js`).
    $('mg-say').innerHTML = 'No games yet. Play <a href="../play/vs-computer.html">the engine</a>'
      + (self.CHESS_APP ? ' or <a href="../play/vs-friend.html">a friend</a>, '
        : ', <a href="../play/vs-friend.html">a friend</a> or <a href="../play/lichess.html">on Lichess</a>, ')
      + 'and every game you play is kept here — finished or not.';
  } else if (!finished.length) {
    $('mg-say').textContent = 'Nothing finished yet.';
  } else if (query || filter !== 'all') {
    $('mg-say').textContent = found.length
      ? `${plural(found.length, 'game')} of ${finished.length.toLocaleString()}.`
      : 'No games match. Try fewer words, or another filter.';
  } else {
    const won = finished.filter((r) => outcomeFor(r) === 'Won').length;
    const lost = finished.filter((r) => outcomeFor(r) === 'Lost').length;
    const drawn = finished.filter((r) => outcomeFor(r) === 'Drawn').length;
    $('mg-say').innerHTML = `${esc(plural(finished.length, 'game'))}: ${won} won, ${drawn} drawn, ${lost} lost. `
      + (levels.games ? `Your level is <b>${formatLevel(levels.level)}</b>, from ${esc(plural(levels.games, 'rated game'))}. ` : '')
      + 'Review one to see where it turned.';
  }

  // Names for the openings, the first time there are games to name.
  if (!openings && games.length) {
    loadOpenings().then((map) => { openings = map; draw(); }).catch(() => { /* rows keep their first moves */ });
  }
}

$('mg-filters').addEventListener('click', (event) => {
  const button = event.target.closest('[data-filter]');
  if (!button) return;
  filter = button.dataset.filter;
  shown = PAGE;
  draw();
});

$('mg-search').addEventListener('input', (event) => {
  query = event.currentTarget.value.trim().toLowerCase();
  shown = PAGE;
  draw();
});

$('mg-h2h-list').addEventListener('click', (event) => {
  const who = event.target.closest('[data-who]');
  if (!who) return;
  $('mg-search').value = who.dataset.who;
  query = who.dataset.who.toLowerCase();
  filter = 'all';
  shown = PAGE;
  draw();
  $('mg-finished').scrollIntoView({ behavior: 'smooth', block: 'start' });
});

$('mg-more').addEventListener('click', () => {
  shown += PAGE;
  draw();
});

/** The games the search and the filter show — all of them, not only the page drawn — as one PGN file. */
$('mg-download').addEventListener('click', async () => {
  const { toPgn } = await import('../play/keep.js');
  const found = matching(allGames().filter((r) => r.over));
  const text = found.map((r) => toPgn(pgnDetails(r))).join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'application/x-chess-pgn' }));
  const a = Object.assign(document.createElement('a'), {
    href: url, download: `chesszz-games-${new Date().toISOString().slice(0, 10)}.pgn`,
  });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
});

accountUser.then((user) => {
  $('mg-where').innerHTML = user
    ? 'Kept on your account, so they are on every device you sign in on. Your stars and notes are yours alone.'
    : 'Kept in this browser. <a href="../account/">Sign in</a> and they are kept on your account too, on every device.';
});

draw();
// Games played on another device, once the account answers.
syncGames().then((changed) => { if (changed) draw(); });
// The account's word on what a game did to your level, when an upload brings it.
addEventListener(LEVEL_EVENT, () => draw());
// And games this page did not write: another tab finishing one.
addEventListener('storage', (event) => { if (event.key === 'immortal-my-games') draw(); });
